import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import { useTheme } from "../context/ThemeContext";
import { getUserRole } from "../utils/Auth";
import { Clock, AlertTriangle } from "lucide-react";

const RemindersPage = () => {
  const token = localStorage.getItem("token");
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { darkMode } = useTheme();
  const role = getUserRole();

  const fetchReminders = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/reminders?hoursBefore=2`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error("Eroare la încărcarea reminderelor.");
        return;
      }

      const priorityMap = {
        0: "Urgent",
        1: "Mare",
        2: "Medie",
        3: "Mica",
      };

      const normalized = Array.isArray(data.reminders)
        ? data.reminders.map((t) => ({
            ...t,
            priority:
              typeof t.priority === "number"
                ? priorityMap[t.priority] || "Medie"
                : t.priority,
            deadline: t.deadline
              ? t.deadline.endsWith("Z")
                ? t.deadline
                : t.deadline + "Z"
              : null,
          }))
        : [];

      setReminders(normalized);
    } catch (err) {
      console.error("⚠️ Reminder fetch error:", err);
      toast.error("Eroare la server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  if (loading)
    return (
      <div className="text-center mt-10 text-gray-500">
        Se încarcă reminder-ele...
      </div>
    );

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("ro-RO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const priorityColors = {
    Urgent: "border-red-500 text-red-500",
    Mare: "border-orange-500 text-orange-500",
    Medie: "border-yellow-500 text-yellow-600",
    Mica: "border-green-500 text-green-600",
  };

  const priorityLabels = {
    Urgent: "Urgent",
    Mare: "Mare",
    Medie: "Medie",
    Mica: "Mică",
  };

  const getDeadlineStyle = (deadline) => {
    if (!deadline) return "";
    const now = new Date();
    const dl = new Date(deadline);
    const diffHours = (dl - now) / 1000 / 3600;
    if (diffHours < 0) return "border-red-500 ring-1 ring-red-500";
    if (diffHours < 1) return "border-orange-500";
    return "border-gray-400";
  };

  return (
    <div
      className={`p-6 min-h-screen transition-colors duration-300 ${
        darkMode ? "bg-gray-900 text-gray-100" : "bg-gray-200 text-gray-800"
      }`}
    >
      <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
        ⏰ Reminderi active
      </h2>

      {reminders.length === 0 ? (
        <p className="text-center mt-10 text-gray-500">
          Nu există reminderi activi în următoarele 2 ore.
        </p>
      ) : (
        <div
          className={`overflow-y-auto custom-scrollbar p-4 h-[calc(100vh-200px)] 
                      grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5`}
        >
          {reminders.map((r) => {
            const dlStyle = getDeadlineStyle(r.deadline);
            const isOverdue = new Date(r.deadline) < new Date();

            return (
              <div
                key={r.id}
                className={`relative rounded-2xl p-5 shadow-lg border-l-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${dlStyle} ${
                  darkMode
                    ? "bg-gray-900 border-gray-700 hover:bg-gray-850"
                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                }`}
                style={{ height: "180px" }}
              >
                {/* TITLE */}
                <h3
                  className="font-semibold text-lg leading-tight truncate pr-2 mb-2"
                  title={r.title}
                >
                  {r.title}
                </h3>

                {/* DESCRIPTION */}
                {r.description && (
                  <p
                    className="text-sm text-gray-400 mb-4 line-clamp-2"
                    title={r.description}
                  >
                    {r.description}
                  </p>
                )}

                {/* DEADLINE (if not overdue) */}
                {!isOverdue && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-sm text-gray-400">
                    <Clock size={15} />
                    <span>{formatDate(r.deadline)}</span>
                  </div>
                )}

                {/* BOTTOM LEFT - DEPAȘIT + DEADLINE */}
                {isOverdue && (
                  <div
                    className={`absolute bottom-3 left-3 flex flex-col text-xs font-semibold px-2 py-1 rounded-md shadow-md ${
                      darkMode
                        ? "bg-red-900/40 text-red-400 border border-red-700"
                        : "bg-red-100 text-red-700 border border-red-300"
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <AlertTriangle size={14} className="animate-pulse" />
                      <span>Depășit</span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 text-[11px] opacity-80">
                      <Clock size={12} />
                      <span>{formatDate(r.deadline)}</span>
                    </div>
                  </div>
                )}

                {/* BOTTOM RIGHT - PRIORITATE */}
                <div
                  className={`absolute bottom-3 right-3 border rounded-lg px-2 py-0.5 text-xs font-semibold ${
                    priorityColors[r.priority] || "border-gray-400 text-gray-400"
                  }`}
                >
                  {priorityLabels[r.priority] || r.priority}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RemindersPage;
